import { SessionService } from "src/containers/authentication/services/session/session-service";
import { HomeStorage } from "src/containers/home/constants/storage";
import { BannerLayer } from "src/containers/home/models/ad-banner/banner-layer";
import { CampaignBanner, LayeredBannerDesign } from "src/containers/home/types";
import { ScratchGameService } from "src/containers/wellbeing/rewards/services/scratch-game-service/scratch-game-service";
import { WellbeingRoutesV1 } from "src/containers/wellbeing/shared/constants/routes";
import { DI } from "src/infra/di/container";
import { Environment } from "src/libs/environment";
import { Platform } from "src/libs/platform";
import {
    gtmEvent,
    TagManagerEvent,
} from "src/shared/services/google-tag-manager/google-tag-manager-service";

const transformResourceNamesIntoLayers = (
    resourceNames: string,
    comesFrom: "LEFT" | "RIGHT" = "LEFT"
) => {
    return resourceNames.split(",").map((_resourceBaseName) => {
        const resourceBaseName = _resourceBaseName.trim();
        return BannerLayer.fromJson<BannerLayer>({
            type: comesFrom === "LEFT" ? "TEXT" : "IMAGE",
            from: comesFrom,
            platform: "MOBILE",
            sources: [`${HomeStorage.StaticBanners}/${resourceBaseName}`],
            alt: resourceBaseName.replace(/(.png)|(.jpg)|(.svg)|(.webp)/i, ""),
        });
    });
};

const isValidToInsertBanner: () => Promise<boolean> = async () => {
    const scratchGameBannerEnabled =
        Environment.bool("SCRATCH_GAME_ENABLED") &&
        Environment.bool("SCRATCH_GAME_BANNER_ENABLED");
    const sessionService = DI.get(SessionService);
    const scratchService = DI.get(ScratchGameService);
    if (!scratchGameBannerEnabled) {
        return false;
    }

    if (!(await scratchService.isUserAllowListed())) {
        return false;
    }

    const isMobile = Platform.isRunningOnFlutter();
    if (!isMobile) {
        return false;
    }
    const user = sessionService.getUser();
    const userIdentity = user?.userIdentity ?? "";
    const games = await scratchService.getGames(userIdentity);

    if (games.length === 0) {
        return false;
    } else {
        return games.some((game) => game.allowed);
    }
};

const insertCustomBannerIntoResponse = async (
    response: CampaignBanner<unknown>[]
) => {
    // If is not valid to insert this banner then just return `response`
    if (!(await isValidToInsertBanner())) {
        return response;
    }

    const scratchGameBannerLeftSide = Environment.string(
        "SCRATCH_GAME_BANNER_LEFT_SIDE"
    );
    const scratchGameBannerRightSide = Environment.string(
        "SCRATCH_GAME_BANNER_RIGHT_SIDE"
    );
    const leftSideLayers: BannerLayer[] = transformResourceNamesIntoLayers(
        scratchGameBannerLeftSide,
        "LEFT"
    );
    const rightSideLayers: BannerLayer[] = transformResourceNamesIntoLayers(
        scratchGameBannerRightSide,
        "RIGHT"
    );

    const tagManagerBannerItem = {
        promotion_id: "RASPAGANA20220104",
        promotion_name: "RASPA Y GANA",
        creative_name: WellbeingRoutesV1.ROUTE_WELLBEING_REWARDS,
        create_slot: 1,
        location_id: "Home Banner",
    };
    const newBanner = {
        code: "RASPAGANA20220104",
        design: {
            image: [],
            layers: [
                ...leftSideLayers.map((layer) => layer.toJson()),
                ...rightSideLayers.map((layer) => layer.toJson()),
            ],
            metadata: {
                redirectUrl: WellbeingRoutesV1.ROUTE_WELLBEING_REWARDS,
                analyticsEvent: {
                    category: "Banners de campaña",
                    action: "Banner - RASPA Y GANA",
                    label: "RASPAGANA - $userIdentity",
                },
                isRedirectExternal: false,
                tagManagerEvent: {
                    event: "view_promotion",
                    ecommerce: {
                        items: [tagManagerBannerItem],
                    },
                },
            },
        },
        typeDesign: "layered",
        themeDesign: "cyan-gradient",
    };
    return [newBanner, ...response];
};

const insertTagManagerEventIntoResponse = async (
    response: CampaignBanner<unknown>[]
) => {
    const tagManagerEvent: TagManagerEvent = {
        event: gtmEvent.viewPromotion,
        ecommerce: {
            items: [],
        },
    };
    return (response as CampaignBanner<LayeredBannerDesign>[]).map(
        (banner, index) => {
            const newBanner = { ...banner };
            const metadata = newBanner.design?.metadata;
            if (metadata) {
                const item = {
                    promotion_id: newBanner.code,
                    promotion_name: metadata.analyticsEvent?.action,
                    creative_name: metadata.redirectUrl,
                    creative_slot: index + 1,
                    location_id: "Home Banner",
                };
                newBanner.design.metadata = {
                    ...metadata,
                    tagManagerEvent: {
                        ...tagManagerEvent,
                        ecommerce: {
                            items: [item],
                        },
                    },
                };
            }
            return newBanner;
        }
    );
};

export {
    transformResourceNamesIntoLayers,
    isValidToInsertBanner,
    insertCustomBannerIntoResponse,
    insertTagManagerEventIntoResponse,
};
